/**
 * Подписи и форматирование полей карточки машины.
 *
 * ── Зачем в домене ────────────────────────────────────────────────────
 *
 * В журнале изменений лежат технические имена: `inspection_expires_at`,
 * `has_tail_lift`, `region_id`. Показывать их человеку нельзя, а
 * переводить прямо в компоненте — значит завести второй словарь, когда
 * те же поля понадобятся в другом месте. Здесь же живут describeCarDue
 * и правила тона: место одно.
 *
 * ── Про поле без подписи ──────────────────────────────────────────────
 *
 * Оно показывается техническим именем, а не пропускается. Скрытая правка
 * хуже некрасивой: пропущенное поле создаёт впечатление, что его не
 * меняли. Есть канареечный тест, требующий подпись для каждого поля
 * карточки, — но пока он не сработал, поведение обязано быть безопасным.
 */

/**
 * Типы кузова. Зеркалит BODY_TYPES бэкенда (app/models/car.py).
 * Порядок — по частоте в парке малотоннажников; «other» последним:
 * это не тип, а его отсутствие.
 *
 * Живёт в домене, а не в странице: список нужен и карточке, и панели
 * быстрой правки, а второй экземпляр разошёлся бы с первым при первой
 * же добавленной строке.
 */
export const CAR_BODY_TYPES = [
  { value: 'box',          label: 'Промтоварный фургон (будка)' },
  { value: 'tent',         label: 'Тент' },
  { value: 'panel_van',    label: 'Цельнометаллический фургон' },
  { value: 'flatbed',      label: 'Бортовая платформа' },
  { value: 'refrigerator', label: 'Рефрижератор' },
  { value: 'other',        label: 'Не указан' },
]

/** Статусы машины для выбора. Подписи те же, что показывает StatusBadge. */
export const CAR_STATUS_OPTIONS = [
  { value: 'available',           label: 'Доступна' },
  { value: 'in_use',              label: 'В поездке' },
  { value: 'maintenance',         label: 'На ТО' },
  { value: 'repair',              label: 'В ремонте' },
  { value: 'inspection_required', label: 'Нужен осмотр' },
  { value: 'blocked',             label: 'Заблокирована' },
]

export const CAR_FIELD_LABELS = {
  brand: 'Марка',
  model: 'Модель',
  plate: 'Госномер',
  vin: 'VIN',
  year: 'Год выпуска',
  color: 'Цвет',
  mileage: 'Пробег',
  status: 'Статус',
  body_type: 'Тип кузова',
  has_tail_lift: 'Гидроборт',
  comment: 'Комментарий',
  region_id: 'Регион',
  photo_main: 'Изображение',
  insurance_expires_at: 'Страховка до',
  inspection_expires_at: 'Техосмотр до',
  oil_change_due_km: 'Замена масла, км',
  oil_change_due_date: 'Замена масла до',
  service_due_date: 'Плановое ТО до',
}

/** Поля, которые в журнал попадать не должны: они производные. */
export const CAR_DERIVED_FIELDS = [
  'id',
  'status_label',
  'body_type_label',
  'region',
  'reminders',
  'created_at',
]

export function carFieldLabel(field) {
  return CAR_FIELD_LABELS[field] || field
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Значение поля в человеческом виде.
 *
 * `null` показывается словом, а не пустотой: разница между «поле
 * очистили» и «здесь ничего не нарисовалось» существенна, а на глаз
 * пустая ячейка неотличима от ошибки отрисовки.
 */
export function carFieldValue(field, value, dictionaries = {}) {
  if (value === null || value === undefined || value === '') return 'не задано'

  if (field === 'status') {
    return dictionaries.statuses?.[value] || value
  }
  if (field === 'region_id') {
    return dictionaries.regions?.[value] || `регион #${value}`
  }
  if (field === 'body_type') {
    return dictionaries.bodyTypes?.[value] || value
  }
  if (field === 'has_tail_lift') {
    return value ? 'есть' : 'нет'
  }
  if (field === 'mileage' || field === 'oil_change_due_km') {
    return `${Number(value).toLocaleString('ru')} км`
  }
  if (field === 'photo_main') {
    // Путь к файлу или строка вида icon:sedan:#1e88e5 — ни то, ни другое
    // человеку ничего не говорит, а длинный путь ломает вёрстку строки.
    return typeof value === 'string' && value.startsWith('icon:') ? 'иконка' : 'фотография'
  }
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    return new Date(value).toLocaleDateString('ru', { dateStyle: 'medium' })
  }

  return String(value)
}

/**
 * Заголовок события истории.
 *
 * Отдельные формулировки, а не «обновлено 1 поле» для всего подряд:
 * «машину заблокировали» и «поправили пробег» не должны выглядеть
 * одинаково в ленте.
 */
export function describeCarEvent(entry) {
  const changed = Object.keys(entry.after || {})

  switch (entry.action) {
    case 'car.created':
      return { title: 'Машина добавлена', kind: 'create' }
    case 'car.status_changed':
      return { title: 'Статус изменён', kind: 'status' }
    case 'car.photo_changed':
      return { title: 'Изображение изменено', kind: 'photo' }
    case 'car.document_added':
      return { title: 'Документ добавлен', kind: 'doc' }
    case 'car.document_deleted':
      return { title: 'Документ удалён', kind: 'doc' }
    case 'car.transferred':
      return { title: 'Машина передана в другой регион', kind: 'transfer' }
    case 'car.damage_registered':
      return { title: 'Зарегистрировано повреждение', kind: 'damage' }
    case 'car.damage_repaired':
      return { title: 'Повреждение устранено', kind: 'damage' }
    case 'car.deleted':
      return { title: 'Машина удалена', kind: 'delete' }
    default:
      return {
        title:
          changed.length === 1
            ? `Изменено: ${carFieldLabel(changed[0]).toLowerCase()}`
            : `Изменено полей: ${changed.length}`,
        kind: 'update',
      }
  }
}
